import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Globe, DollarSign, Edit2, Plus, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { RegionForm } from './RegionForm';
import { RegionalPriceForm } from './RegionalPriceForm';

interface Region {
  id: string;
  code: string;
  name: string;
  currency_code: string;
  currency_symbol: string;
  exchange_rate: number;
  is_active: boolean;
}

interface Plan {
  id: string;
  name: string;
  description: string;
  credits: number;
  duration_days: number;
  price_usd: number;
  is_active: boolean;
}

interface RegionalPrice {
  id: string;
  plan_id: string;
  region_id: string;
  price: number;
}

export const RegionalPricing: React.FC = () => {
  const [showRegionForm, setShowRegionForm] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Fetch regions
  const { data: regions = [], isLoading: regionsLoading } = useQuery<Region[]>(
    'regions',
    async () => {
      const { data, error } = await supabase
        .from('regions')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    }
  );

  // Fetch plans
  const { data: plans = [], isLoading: plansLoading } = useQuery<Plan[]>(
    'subscription-plans',
    async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price_usd');

      if (error) throw error;
      return data;
    }
  );

  // Fetch regional prices
  const { data: prices = [], isLoading: pricesLoading } = useQuery<RegionalPrice[]>(
    'regional-prices',
    async () => {
      const { data, error } = await supabase
        .from('regional_prices')
        .select('*');

      if (error) throw error;
      return data;
    }
  );

  const handleEditPrice = (region: Region, plan: Plan) => {
    setSelectedRegion(region);
    setSelectedPlan(plan);
    setShowPriceForm(true);
  };

  const getPrice = (regionId: string, planId: string) => {
    return prices.find(p => p.region_id === regionId && p.plan_id === planId)?.price || 0;
  };

  const isLoading = regionsLoading || plansLoading || pricesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Regional Pricing
            </h2>
          </div>
          <button
            onClick={() => setShowRegionForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Region
          </button>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Manage pricing for different regions and currencies
        </p>
      </div>

      {/* Pricing Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Region
              </th>
              {plans.map(plan => (
                <th key={plan.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {plan.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {regions.map(region => (
              <tr key={region.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {region.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {region.currency_code} ({region.currency_symbol})
                    </div>
                  </div>
                </td>
                {plans.map(plan => {
                  const price = getPrice(region.id, plan.id);
                  return (
                    <td key={plan.id} className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-900 dark:text-white">
                          {region.currency_symbol}{(price / 100).toFixed(2)}
                        </span>
                        <button
                          onClick={() => handleEditPrice(region, plan)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Region Form Modal */}
      {showRegionForm && (
        <RegionForm
          region={selectedRegion}
          onClose={() => {
            setShowRegionForm(false);
            setSelectedRegion(null);
          }}
        />
      )}

      {/* Price Form Modal */}
      {showPriceForm && selectedRegion && selectedPlan && (
        <RegionalPriceForm
          region={selectedRegion}
          plan={selectedPlan}
          currentPrice={getPrice(selectedRegion.id, selectedPlan.id)}
          onClose={() => {
            setShowPriceForm(false);
            setSelectedRegion(null);
            setSelectedPlan(null);
          }}
        />
      )}
    </div>
  );
};